import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import {
  ref,
  onValue,
  get,
  query,
  orderByChild,
  limitToLast,
  endAt,
  push,
  update,
  set,
  onDisconnect,
  serverTimestamp,
  DataSnapshot,
} from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { chatAuth, chatDb, chatStorage } from 'src/config/firebaseChat.config';
import { ensureChatSignIn, markChatAsRead } from 'src/hooks/useFirebaseMessages';
import { useSelector, AppState } from 'src/store/Store';
import { ChatMessage } from 'src/types/apps/messages';
import { pickLocalizedName } from 'src/utils/chatAvatar';

const PAGE_SIZE = 50;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
let activeChatClaimGeneration = 0;

const normalizeCreatedAt = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.length > 0) {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
  }
  // Firebase serverTimestamp() is an object until the server writes a number.
  // Use now so the new bubble stays at the bottom instead of epoch 1970.
  if (value && typeof value === 'object') return Date.now();

  return 0;
};

const mapSnapshotToMessages = (val: Record<string, any> | null): ChatMessage[] => {
  if (!val) return [];

  return Object.keys(val)
    .map((key) => {
      const raw = val[key];

      return {
        id: key,
        text: raw.message || '',
        createdAt: normalizeCreatedAt(raw.created_at),
        type: (raw.type || 'text') as 'text' | 'image',
        imageUrl: raw.image_url,
        user: {
          id: String(raw.user_id),
          name:
            raw.sender_type === 'stadium'
              ? pickLocalizedName(raw.stadium_name_en || raw.user_name, raw.stadium_name_ar)
              : raw.user_name,
        },
        senderType: (raw.sender_type === 'stadium' ? 'stadium' : undefined) as
          | 'stadium'
          | undefined,
        senderName: raw.sender_type === 'stadium' ? raw.sender_name : undefined,
      };
    })
    .sort((a, b) => a.createdAt - b.createdAt);
};

const mergeMessages = (live: ChatMessage[], history: ChatMessage[]): ChatMessage[] => {
  const byId = new Map<string, ChatMessage>();
  for (const m of history) byId.set(m.id, m);
  for (const m of live) byId.set(m.id, m);

  return Array.from(byId.values()).sort((a, b) => a.createdAt - b.createdAt);
};

// Given a room id, subscribes to its messages, exposes sendMessage/loadEarlier,
// and clears the room's unread counters on mount — mirrors
// mocks/malaeb-player's MessagesDetailsHook.ts. This and useFirebaseMessages.ts
// are the only places that touch the chat RTDB SDK directly.
const useChatDetails = (chatId: string | null, roomStadiumId?: number) => {
  const uid = useSelector((state: AppState) => state.authReducer.user?.data?.id);
  const userName = useSelector((state: AppState) => state.authReducer.user?.data?.name);
  // The dashboard speaks as the stadium, not the individual staff member —
  // every outgoing message is stamped with the selected stadium's identity.
  const selectedStadium = useSelector((state: AppState) => state.configReducer.selectedStadium);
  const inbox = useSelector((state: AppState) => state.messagesReducer.inbox);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [hasMoreEarlier, setHasMoreEarlier] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const liveSliceRef = useRef<ChatMessage[]>([]);
  const historyRef = useRef<ChatMessage[]>([]);
  const allMessagesRef = useRef<ChatMessage[]>([]);
  const loadingEarlierRef = useRef(false);

  useEffect(() => {
    if (!chatDb || !chatId || !uid) {
      setMessages([]);
      setLoading(false);

      return;
    }

    let cancelled = false;
    liveSliceRef.current = [];
    historyRef.current = [];
    allMessagesRef.current = [];
    loadingEarlierRef.current = false;
    setMessages([]);
    setHasMoreEarlier(false);
    setLoading(true);

    const messagesRef = query(
      ref(chatDb, `messages/${chatId}`),
      orderByChild('created_at'),
      limitToLast(PAGE_SIZE),
    );

    const handleMessages = (snapshot: DataSnapshot) => {
      if (cancelled) return;
      setLoading(false);
      const newLive = mapSnapshotToMessages(snapshot.val());

      if (liveSliceRef.current.length > 0) {
        const newLiveIds = new Set(newLive.map((m) => m.id));
        const dropped = liveSliceRef.current.filter((m) => !newLiveIds.has(m.id));
        if (dropped.length > 0) {
          const historyById = new Map<string, ChatMessage>();
          for (const m of historyRef.current) historyById.set(m.id, m);
          for (const m of dropped) historyById.set(m.id, m);
          historyRef.current = Array.from(historyById.values()).sort(
            (a, b) => a.createdAt - b.createdAt,
          );
        }
      }

      liveSliceRef.current = newLive;
      const merged = mergeMessages(newLive, historyRef.current);
      allMessagesRef.current = merged;
      setMessages(merged);

      if (newLive.length < PAGE_SIZE) {
        setHasMoreEarlier(false);
      } else if (historyRef.current.length === 0) {
        setHasMoreEarlier(true);
      }
    };

    const unsubscribeMessages = onValue(messagesRef, handleMessages, () => {
      if (!cancelled) {
        setLoading(false);
        toast.error(t('Chat.failedToOpen'));
      }
    });

    // Claim active_chat BEFORE clearing unreads so Cloud Functions skip
    // increments while this room is open. Clearing first left a window
    // where an inbound message could bump the badge again.
    const roomId = String(chatId);
    const userId = String(uid);
    const activeChatRef = ref(chatDb, `users/${userId}/active_chat`);
    // Module-local generation so a slow set() from a closed room cannot stomp
    // a newer room's active_chat (Bugbot: active_chat overwritten on switch).
    const claimGeneration = ++activeChatClaimGeneration;

    (async () => {
      try {
        await ensureChatSignIn(userId);
        if (cancelled || claimGeneration !== activeChatClaimGeneration) return;
        await set(activeChatRef, roomId);
        if (cancelled || claimGeneration !== activeChatClaimGeneration) {
          // A newer room claimed active_chat — do not mark-read or attach
          // onDisconnect for a claim we no longer own.
          return;
        }
        onDisconnect(activeChatRef).remove();
        await markChatAsRead(roomId, userId);
      } catch {
        // Auth/clear failure must not break the message listener (PRD §9).
      }
    })();

    // Live participant count for the group-chat header (§3) — membership can
    // change after room creation (players leaving), so this is a listener,
    // not a one-shot read.
    const unsubscribeUsers = onValue(ref(chatDb, `chats/${chatId}/users`), (snapshot) => {
      setParticipantCount(snapshot.size);
    });

    return () => {
      cancelled = true;
      onDisconnect(activeChatRef).cancel();
      // Only clear if this effect still owns the claim — otherwise a newer
      // conversation's active_chat would be wiped on the previous room's cleanup.
      if (claimGeneration === activeChatClaimGeneration) {
        set(activeChatRef, null).catch(() => undefined);
      }
      // Player calls setReadMessages() again on cleanup — ensures a race on
      // open (auth not ready yet) still zeroes the badge when leaving the room.
      markChatAsRead(roomId, userId).catch(() => undefined);
      unsubscribeMessages();
      unsubscribeUsers();
    };
  }, [chatId, uid]);

  const loadEarlier = useCallback(async () => {
    if (!chatDb || !chatId || loadingEarlierRef.current || !hasMoreEarlier) return;

    const oldest = allMessagesRef.current[0];
    if (!oldest || !oldest.createdAt) {
      setHasMoreEarlier(false);

      return;
    }

    const endTimestamp = oldest.createdAt - 1;
    if (endTimestamp <= 0) {
      setHasMoreEarlier(false);

      return;
    }

    loadingEarlierRef.current = true;
    setLoadingEarlier(true);

    try {
      const snapshot = await get(
        query(
          ref(chatDb!, `messages/${chatId}`),
          orderByChild('created_at'),
          endAt(endTimestamp),
          limitToLast(PAGE_SIZE),
        ),
      );

      const batch = mapSnapshotToMessages(snapshot.val());
      if (batch.length === 0) {
        setHasMoreEarlier(false);

        return;
      }
      if (batch.length < PAGE_SIZE) {
        setHasMoreEarlier(false);
      }

      const loadedIds = new Set([
        ...liveSliceRef.current.map((m) => m.id),
        ...historyRef.current.map((m) => m.id),
      ]);
      const newMessages = batch.filter((m) => !loadedIds.has(m.id));
      if (newMessages.length === 0) {
        setHasMoreEarlier(false);

        return;
      }

      historyRef.current = [...historyRef.current, ...newMessages];
      const merged = mergeMessages(liveSliceRef.current, historyRef.current);
      allMessagesRef.current = merged;
      setMessages(merged);
    } catch (error) {
      toast.error(t('Chat.failedToLoadEarlier'));
    } finally {
      loadingEarlierRef.current = false;
      setLoadingEarlier(false);
    }
  }, [chatId, hasMoreEarlier]);

  // Shared write path for both text and image messages — writes the exact
  // shape mocks/malaeb-player already writes, so the existing Cloud Functions
  // (last-message, unread fan-out, push) keep working untouched.
  const writeMessage = useCallback(
    async (
      text: string,
      type: 'text' | 'image',
      imageUrl?: string,
      roomIdOverride?: string,
      stadiumIdOverride?: number,
    ) => {
      const effectiveChatId = roomIdOverride || chatId;
      if (!chatDb || !effectiveChatId || !uid || !selectedStadium) {
        toast.error(t('Chat.failedToSend'));
        throw new Error('Chat send preconditions not met');
      }

      // Prefer the room's own stadium — from the inbox, or the value the
      // caller just resolved (stadiumIdOverride), which reflects the real
      // room stamp even when `roomStadiumId` state has not re-rendered yet
      // (e.g. a deferred room resolved and sent to in the same call chain —
      // Bugbot: stale roomStadiumId lets a cross-stadium send through, then
      // the Chats page closes the conversation and the message looks lost).
      const knownStadiumId =
        inbox.find((row) => row.chatId === effectiveChatId)?.stadiumId ??
        stadiumIdOverride ??
        roomStadiumId;
      if (knownStadiumId != null && knownStadiumId !== selectedStadium.id) {
        toast.error(t('Chat.failedToSend'));
        throw new Error('Chat send stadium mismatch');
      }

      const messagesListRef = ref(chatDb, `messages/${effectiveChatId}`);
      const newKey = push(messagesListRef).key;
      if (!newKey) {
        toast.error(t('Chat.failedToSend'));
        throw new Error('Chat send failed to allocate key');
      }

      const payload: Record<string, unknown> = {
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        user_id: String(uid),
        // Legacy single-locale fallback — the only remaining consumer of this
        // field is the Cloud Function push-notification title, which has no
        // per-recipient locale awareness. UI rendering uses stadium_name_en/ar.
        user_name: selectedStadium.title || userName,
        message: text,
        type,
        sender_type: 'stadium',
        sender_name: userName,
        stadium_id: selectedStadium.id,
        stadium_name_en: selectedStadium.title,
        stadium_name_ar: selectedStadium.title_ar,
      };
      if (imageUrl) {
        payload.image_url = imageUrl;
      }

      try {
        await update(messagesListRef, { [newKey]: payload });
      } catch (error) {
        toast.error(t('Chat.failedToSend'));
        throw error;
      }
    },
    [chatId, uid, userName, selectedStadium, inbox, roomStadiumId],
  );

  const sendMessage = useCallback(
    async (text: string, roomIdOverride?: string, stadiumIdOverride?: number) => {
      const effectiveChatId = roomIdOverride || chatId;
      if (!chatDb || !effectiveChatId || !uid || !selectedStadium) {
        toast.error(t('Chat.failedToSend'));
        throw new Error('Chat send preconditions not met');
      }

      try {
        await ensureChatSignIn(uid);
      } catch (error) {
        // ensureChatSignIn now rejects on an auth mismatch instead of
        // failing open (Bugbot) — surface it here since writeMessage never
        // runs to toast it itself.
        toast.error(t('Chat.failedToSend'));
        throw error;
      }
      await writeMessage(text, 'text', undefined, effectiveChatId, stadiumIdOverride);
    },
    [chatId, uid, selectedStadium, writeMessage],
  );

  // Uploads to the same `chat_images/{roomId}/{filename}` convention
  // mocks/malaeb-player's Storage.ts already uses, so the existing Storage
  // security rules (already permitting authenticated writes there) apply
  // without any rules change (plan §Image attachments).
  const sendImageMessage = useCallback(
    async (file: File, caption = '', roomIdOverride?: string, stadiumIdOverride?: number) => {
      const effectiveChatId = roomIdOverride || chatId;
      if (!chatDb || !chatStorage || !effectiveChatId || !uid || !selectedStadium) {
        toast.error(t('Chat.failedToSend'));
        throw new Error('Chat send preconditions not met');
      }

      if (!file.type.startsWith('image/')) {
        toast.error(t('Chat.invalidImageType'));
        throw new Error('Invalid image type');
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast.error(t('Chat.imageTooLarge'));
        throw new Error('Image too large');
      }

      try {
        await ensureChatSignIn(uid);
      } catch (error) {
        // Same as sendMessage — surface the now-rejecting auth mismatch
        // before the upload try/catch below, so it reads as a send failure
        // rather than an upload failure.
        toast.error(t('Chat.failedToSend'));
        throw error;
      }

      if (!chatAuth?.currentUser) {
        toast.error(t('Chat.failedToUploadImage'));
        throw new Error('Not authenticated for storage upload');
      }

      const extension = file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
      const path = `chat_images/${effectiveChatId}/${uid}_${Date.now()}.${extension}`;
      const fileRef = storageRef(chatStorage, path);

      setIsUploading(true);
      setUploadProgress(0);

      let downloadUrl: string;
      try {
        const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type });

        downloadUrl = await new Promise<string>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              setUploadProgress(
                Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
              );
            },
            reject,
            () => {
              getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject);
            },
          );
        });
      } catch (error: any) {
        toast.error(t('Chat.failedToUploadImage'));
        throw error;
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
      }

      // writeMessage toasts failedToSend on its own — keep this out of the
      // upload catch so a post-upload write failure is not also shown as
      // "couldn't be uploaded".
      await writeMessage(caption, 'image', downloadUrl, effectiveChatId, stadiumIdOverride);
    },
    [chatId, uid, selectedStadium, writeMessage],
  );

  return {
    messages,
    loading,
    loadingEarlier,
    hasMoreEarlier,
    participantCount,
    isUploading,
    uploadProgress,
    loadEarlier,
    sendMessage,
    sendImageMessage,
  };
};

export default useChatDetails;
